import {
  AGENT_DATA_FOLDER_MESSAGES,
  AGENT_UPLOAD_REFUSAL_MESSAGES,
  type AgentUploadState,
  type GetFileListFromSSHResponseBody,
  type GetUserCredentialsResponse,
  type UploadTarget,
} from "common";

/**
 * What the SSH data page says about a listing that came through an agent (spec 2026-09-28, 4.4).
 * `truncated` and `skipped` only ever arrive from an agent run environment, so an SSH listing
 * produces no notice here.
 *
 * `skipped` means different things on the two paths (agent-4-5). On a plain browse it counts only
 * the entries the agent itself left out. On a folder add the walk also skips links whose target
 * overlaps a folder already listed, so only that wording mentions them.
 */
export const skippedNotice = (
  count: number,
  context: "browse" | "folderAdd"
): string => {
  const leftOut = `${count} ${count === 1 ? "entry was" : "entries were"} left out`;
  return context === "folderAdd"
    ? `${leftOut}, including links to folders already listed.`
    : `${leftOut}.`;
};

export const browseNotices = (
  list?: GetFileListFromSSHResponseBody
): string[] => {
  const notices: string[] = [];
  if (list?.truncated) notices.push(AGENT_DATA_FOLDER_MESSAGES.browseTruncated);
  if (list?.skipped) notices.push(skippedNotice(list.skipped, "browse"));
  return notices;
};

/** A partial folder is never imported: a truncated recursive listing refuses the add. */
export const folderAddRefusal = (
  list?: GetFileListFromSSHResponseBody
): string | null =>
  list?.truncated ? AGENT_DATA_FOLDER_MESSAGES.folderTooLarge : null;

type ErrorResponse = { response?: { data?: { message?: string } } };
export const isOutsideRootsError = (error: unknown): boolean =>
  (error as ErrorResponse | undefined)?.response?.data?.message ===
  AGENT_DATA_FOLDER_MESSAGES.outsideRoots;

export const isAgentEnvironment = (
  credentials: GetUserCredentialsResponse | undefined,
  runEnvId?: number
): boolean =>
  Boolean(
    credentials?.profiles.some(
      profile => profile.id === runEnvId && profile.transport === "agent"
    )
  );

export const EMPTY_LISTING: GetFileListFromSSHResponseBody = {
  Prefix: "",
  CommonPrefixes: [],
  Contents: [],
};

export interface AgentUploadButtonView {
  visible: boolean;
  enabled: boolean;
  reason: string | null;
  docsLink: boolean;
}

/**
 * The Upload button on an agent data source (agent-5-5, spec 7), driven by the browse answer's
 * `upload.state`. No state means the listing named no data source, so there is no target and no button.
 */
export const agentUploadButtonView = (
  state: AgentUploadState | undefined
): AgentUploadButtonView => {
  if (!state)
    return { visible: false, enabled: false, reason: null, docsLink: false };
  if (state === "ready")
    return { visible: true, enabled: true, reason: null, docsLink: false };
  return {
    visible: true,
    enabled: false,
    reason: AGENT_UPLOAD_REFUSAL_MESSAGES[state],
    docsLink: state === "upgrade_required",
  };
};

export const uploadTargetForDataSource = (
  dataSourceId: number
): UploadTarget => ({
  kind: "data_source",
  dataSourceId,
});

export const uploadTargetForRun = (
  runId: string | number
): UploadTarget | undefined => {
  const id = Number(runId);
  return Number.isInteger(id) && id > 0
    ? { kind: "run_input", runId: id }
    : undefined;
};
